import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { Person } from "../../types/person";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import { PlaceOfPerson } from "../../types/placeOfPerson";
import { Place } from "../../types/place";
import usePlaceOfPersonQuery from "../../hooks/query/connections/usePlaceOfPersonQuery";
import usePlaceOfPersonLogic from "../../hooks/logic/connections/placeOfPersonLogic";

interface PlaceOfPersonController {
  from?: Person | null;
  to?: Place | null;
}

const usePlaceOfPersonController = ({
  from = null,
  to = null,
}: PlaceOfPersonController): ConnectionFormDataController<
  PlaceOfPerson,
  Person,
  Place
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Person | Place = (from || to)!;
  const { useFetchById, useUpdate, useCreate } = usePlaceOfPersonQuery();
  const { defaultPlaceOfPerson } = usePlaceOfPersonLogic(
    from,
    to
  );
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const [isDialogShown, setShowDialog] = useState<boolean>(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<PlaceOfPerson> => {
    if (id === null) {
      return defaultPlaceOfPerson;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Place of Person Relation data not found");
    }
    return connectionData;
  };

  const onSubmit = async (data: Partial<PlaceOfPerson>) => {
    const metadata: Partial<Omit<PlaceOfPerson, "_from" | "_to">> = {
      notes: data.notes,
      system_place_of_person_type: data.system_place_of_person_type,
      internal_notes: data.internal_notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
    };
    const updateData: CreateEdgeData<PlaceOfPerson> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Place of Person Relation");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Place of Person Relation");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = () =>
    `${isCreateMode ? "Create New" : "Edit"} Person Of Place Relation`;

  return {
    defaultValues: defaultPlaceOfPerson,
    isCreateMode,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default usePlaceOfPersonController;
